(function initChewingMatters() {
  function isInView(section) {
    const rect = section.getBoundingClientRect();
    const view = window.innerHeight || 1;
    return rect.top < view * 0.85 && rect.bottom > view * 0.15;
  }

  function syncKickerFlush(section) {
    const kicker = section.querySelector("[data-chewing-kicker]");
    if (!kicker) return;
    const sectionRect = section.getBoundingClientRect();
    const parent = kicker.offsetParent || section;
    const parentRect = parent.getBoundingClientRect();
    const shift = Math.round(sectionRect.left - parentRect.left);
    kicker.style.setProperty("--chew-kicker-shift", shift + "px");
  }

  function setup(root) {
    if (!root || typeof root.querySelectorAll !== "function") return;

    const sections = root.matches?.("[data-chewing-matters]")
      ? [root]
      : [...root.querySelectorAll("[data-chewing-matters]")];

    sections.forEach((section) => {
      if (section.dataset.ready === "true") return;
      section.dataset.ready = "true";

      const chart = section.querySelector("[data-chewing-chart]");
      const line = section.querySelector("[data-chart-line]");
      const animateEnabled = section.dataset.animate !== "false";
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      let observer = null;
      let safetyTimer = null;
      let failsafeTimer = null;
      let replayLockTimer = null;
      let played = false;
      let replaying = false;
      let lineLength = 0;
      let onResize = null;
      let onChartEnter = null;

      const cleanup = () => {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (safetyTimer) {
          window.clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        if (failsafeTimer) {
          window.clearTimeout(failsafeTimer);
          failsafeTimer = null;
        }
        if (replayLockTimer) {
          window.clearTimeout(replayLockTimer);
          replayLockTimer = null;
        }
        if (onResize) {
          window.removeEventListener("resize", onResize);
          onResize = null;
        }
        if (chart && onChartEnter) {
          chart.removeEventListener("pointerenter", onChartEnter);
          onChartEnter = null;
        }
        section.classList.remove("is-reveal-armed");
        section.dataset.ready = "false";
      };
      section._chewingMattersCleanup = cleanup;

      const measureLength = () => {
        if (!line || typeof line.getTotalLength !== "function") return 0;
        try {
          const length = line.getTotalLength();
          return Number.isFinite(length) && length > 0 ? length : 0;
        } catch (_error) {
          return 0;
        }
      };

      const showLineImmediate = () => {
        if (!line) return;
        if (chart) chart.classList.add("is-drawn");
        line.style.transition = "none";
        line.style.strokeDasharray = lineLength ? String(lineLength) : "none";
        line.style.strokeDashoffset = "0";
        line.style.opacity = "1";
      };

      const prepareHiddenLine = () => {
        if (!line) return 0;
        const length = measureLength();
        lineLength = length;
        if (!length) {
          showLineImmediate();
          return 0;
        }
        line.style.transition = "none";
        line.style.opacity = "1";
        line.style.strokeDasharray = String(length);
        line.style.strokeDashoffset = String(length);
        void line.getBoundingClientRect();
        return length;
      };

      const drawChart = () => {
        if (!chart) return;
        chart.classList.add("is-drawn");

        if (!line || !animateEnabled || reduceMotion) {
          showLineImmediate();
          return;
        }

        const length = lineLength || prepareHiddenLine();
        if (!length) {
          showLineImmediate();
          return;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            line.style.transition = "stroke-dashoffset 1.4s cubic-bezier(0.22, 1, 0.36, 1)";
            line.style.strokeDashoffset = "0";
          });
        });

        if (failsafeTimer) window.clearTimeout(failsafeTimer);
        failsafeTimer = window.setTimeout(showLineImmediate, 1600);
      };

      const resetChart = () => {
        if (!chart) return;
        chart.classList.remove("is-drawn");
        if (!line || !lineLength) return;
        line.style.transition = "none";
        line.style.strokeDasharray = String(lineLength);
        line.style.strokeDashoffset = String(lineLength);
        void line.getBoundingClientRect();
      };

      const showStatic = () => {
        section.classList.add("is-visible");
        if (chart) chart.classList.add("is-drawn");
        showLineImmediate();
      };

      const replayOnHover = () => {
        if (!animateEnabled || reduceMotion || !lineLength || replaying || !played) return;
        replaying = true;
        if (failsafeTimer) {
          window.clearTimeout(failsafeTimer);
          failsafeTimer = null;
        }
        resetChart();
        // Draw immediately so a stalled/background rAF cannot leave the line hidden.
        drawChart();
        if (replayLockTimer) window.clearTimeout(replayLockTimer);
        replayLockTimer = window.setTimeout(() => {
          replaying = false;
          replayLockTimer = null;
        }, 1600);
      };

      try {
        syncKickerFlush(section);
        onResize = () => syncKickerFlush(section);
        window.addEventListener("resize", onResize, { passive: true });

        if (animateEnabled && !reduceMotion) {
          prepareHiddenLine();
        } else {
          lineLength = measureLength();
          showLineImmediate();
        }

        if (chart && animateEnabled && !reduceMotion) {
          onChartEnter = () => replayOnHover();
          chart.addEventListener("pointerenter", onChartEnter);
        }

        if (!animateEnabled || reduceMotion || !("IntersectionObserver" in window)) {
          showStatic();
          return;
        }

        section.classList.add("is-reveal-armed");

        const play = () => {
          if (played) return;
          played = true;
          syncKickerFlush(section);
          section.classList.add("is-visible");
          if (safetyTimer) {
            window.clearTimeout(safetyTimer);
            safetyTimer = null;
          }
          drawChart();
          if (observer) {
            observer.disconnect();
            observer = null;
          }
        };

        safetyTimer = window.setTimeout(play, 2500);

        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              play();
            });
          },
          { threshold: 0.15, rootMargin: "0px 0px -5% 0px" }
        );
        observer.observe(section);

        requestAnimationFrame(() => {
          syncKickerFlush(section);
          if (isInView(section)) play();
        });
      } catch (_error) {
        showStatic();
      }
    });
  }

  const start = () => setup(document);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", (event) => setup(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target;
    if (!root || typeof root.querySelectorAll !== "function") return;
    const sections = root.matches?.("[data-chewing-matters]")
      ? [root]
      : [...root.querySelectorAll("[data-chewing-matters]")];
    sections.forEach((section) => {
      if (typeof section._chewingMattersCleanup === "function") {
        section._chewingMattersCleanup();
      }
    });
  });
})();
